import { usePermissions } from '../../permissions/usePermissions';
import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, message, Space, Alert, Tag, Spin } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useListArticleDBFFiles, usePreviewArticlesFromDBF, useImportArticlesFromDBF } from '../../hooks/articleHooks';

// Column labels for the preview table (fields without a label use the field name)
const FIELD_LABELS = {
  code_art: 'Code Article',
  ARTICLE: 'Article',
  photo: 'Photo',
  qt_cart: 'Qté/Carton',
  PA_HT: 'PA HT',
  TVA: 'TVA',
  PA_TTC: 'PA TTC',
  PV_HT: 'PV HT',
  PV_TTC: 'PV TTC',
  CODE_MAR: 'Code Marque',
  STK_MIN: 'Stock Min',
  STK_MAX: 'Stock Max',
  STK_SEC: 'Stock Séc.',
  q_paquet: 'Qté/Paquet',
  Remise: 'Remise',
  REF_OEM: 'Réf. OEM',
  VENTE_BLOQ: 'Vente Bloquée',
  ACHAT_BLOQ: 'Achat Bloqué',
  uav: 'UAV',
  Remarque: 'Remarque',
  substitution: 'Substitution',
  code_fam: 'Famille',
  code_sfam: 'Sous-Famille',
  code_frs: 'Fournisseur',
};

const BOOLEAN_FIELDS = ['VENTE_BLOQ', 'ACHAT_BLOQ'];
const FLOAT_FIELDS = ['PA_HT', 'TVA', 'PA_TTC', 'PV_HT', 'PV_TTC', 'Remise'];

const ArticleDBFImportModal = ({ visible, onClose, onImported }) => {
  const { consultation } = usePermissions('articles');
  const { fetchFiles, loading: loadingFiles } = useListArticleDBFFiles();
  const { previewArticles, loading: loadingPreview } = usePreviewArticlesFromDBF();
  const { importArticles, loading: loadingImport } = useImportArticlesFromDBF();

  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [step, setStep] = useState(1); // 1: select file, 2: preview, 3: result

  const handleFetchFiles = async () => {
    try {
      const result = await fetchFiles();
      setFiles(result || []);
      if (!result || result.length === 0) {
        message.warning('Aucun fichier DBF trouvé dans le répertoire FTP');
      }
    } catch (error) {
      message.error(`Erreur lors de la récupération des fichiers: ${error.message}`);
    }
  };

  const handleSelectFile = async (fileName) => {
    setSelectedFile(fileName);
    try {
      message.loading({ content: 'Lecture du fichier DBF...', key: 'article-import' });
      const result = await previewArticles(fileName);
      message.success({ content: `${result.articles.length} articles lus (${result.newCount} nouveaux)`, key: 'article-import' });
      setPreview(result);
      setStep(2);
    } catch (error) {
      message.error({ content: `Erreur lors de la lecture: ${error.message}`, key: 'article-import' });
    }
  };

  const handleConfirmImport = async () => {
    const newArticles = (preview?.articles || []).filter(a => !a.exists);
    if (newArticles.length === 0) {
      message.warning('Aucun nouvel article à créer');
      return;
    }
    try {
      message.loading({ content: 'Création des articles...', key: 'article-import' });
      const result = await importArticles(newArticles);
      message.success({ content: `${result.created} nouveaux articles créés`, key: 'article-import' });
      setImportResult(result);
      setStep(3);
      if (result.created > 0 && onImported) {
        onImported(result);
      }
    } catch (error) {
      message.error({ content: `Erreur lors de la création: ${error.message}`, key: 'article-import' });
    }
  };

  const handleClose = () => {
    setFiles([]);
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
    setStep(1);
    onClose();
  };

  const fileColumns = [
    {
      title: 'Nom du fichier',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <FileTextOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Taille',
      dataIndex: 'size',
      key: 'size',
      render: (size) => `${(size / 1024).toFixed(2)} KB`,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleSelectFile(record.name)}
          loading={loadingPreview && selectedFile === record.name}
          disabled={loadingPreview}
        >
          Prévisualiser
        </Button>
      ),
    },
  ];

  const renderPreviewValue = (field) => (val) => {
    if (val === null || val === undefined) return <span style={{ color: '#bbb' }}>—</span>;
    if (BOOLEAN_FIELDS.includes(field)) {
      return val ? <Tag color="red">Oui</Tag> : <Tag>Non</Tag>;
    }
    if (FLOAT_FIELDS.includes(field) && typeof val === 'number') {
      return val.toFixed(2);
    }
    return String(val);
  };

  const previewColumns = [
    {
      title: 'Statut',
      dataIndex: 'exists',
      key: 'exists',
      width: 90,
      fixed: 'left',
      filters: [
        { text: 'Nouveau', value: false },
        { text: 'Existant', value: true },
      ],
      onFilter: (value, record) => record.exists === value,
      render: (exists) =>
        exists ? <Tag color="orange">Existant</Tag> : <Tag color="green">Nouveau</Tag>,
    },
    ...(preview?.mappedFields || []).filter(field => !consultation || !['PA_HT', 'PA_TTC'].includes(field)).map((field) => ({
      title: FIELD_LABELS[field] || field,
      dataIndex: field,
      key: field,
      width: field === 'ARTICLE' ? 220 : 110,
      ellipsis: true,
      fixed: field === 'code_art' ? 'left' : undefined,
      render: renderPreviewValue(field),
    })),
  ];

  useEffect(() => {
    if (visible && step === 1 && files.length === 0) {
      handleFetchFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const newCount = preview?.newCount || 0;

  return (
    <Modal
      title={
        step === 1
          ? 'Importer nouveaux articles depuis FTP (DBF)'
          : step === 2
            ? `Prévisualisation — ${preview?.fileName || ''}`
            : "Résultat de l'importation"
      }
      open={visible}
      onCancel={handleClose}
      width={step === 2 ? 1300 : 700}
      footer={
        step === 1 ? [
          <Button key="refresh" onClick={handleFetchFiles} loading={loadingFiles}>
            Actualiser
          </Button>,
          <Button key="cancel" onClick={handleClose}>
            Annuler
          </Button>,
        ] : step === 2 ? [
          <Button key="back" onClick={() => { setStep(1); setPreview(null); }}>
            Retour
          </Button>,
          <Button key="cancel" onClick={handleClose}>
            Annuler
          </Button>,
          <Button
            key="import"
            type="primary"
            onClick={handleConfirmImport}
            loading={loadingImport}
            disabled={newCount === 0}
          >
            {`Créer ${newCount} nouveaux articles`}
          </Button>,
        ] : [
          <Button key="back" onClick={() => { setStep(1); setPreview(null); setImportResult(null); }}>
            Importer un autre fichier
          </Button>,
          <Button key="close" type="primary" onClick={handleClose}>
            Fermer
          </Button>,
        ]
      }
    >
      {step === 1 && (
        <div>
          <Alert
            message="Sélectionnez le fichier DBF contenant les nouveaux articles"
            description="Les fichiers sont situés dans le répertoire /celer/achat sur le serveur FTP. Une prévisualisation sera affichée avant la création."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            columns={fileColumns}
            dataSource={files}
            rowKey="name"
            loading={loadingFiles}
            pagination={false}
            locale={{
              emptyText: loadingFiles ? <Spin /> : 'Aucun fichier trouvé',
            }}
          />
        </div>
      )}

      {step === 2 && (
        <div>
          <Alert
            message={`${preview?.articles?.length || 0} articles lus — ${newCount} nouveaux seront créés, ${preview?.existingCount || 0} existent déjà (ignorés)`}
            description={
              <div>
                {preview?.skippedNoCode > 0 && (
                  <p style={{ margin: 0 }}>Enregistrements sans code article (ignorés): {preview.skippedNoCode}</p>
                )}
                {preview?.duplicates > 0 && (
                  <p style={{ margin: 0 }}>Doublons dans le fichier (ignorés): {preview.duplicates}</p>
                )}
                <p style={{ margin: 0 }}>Colonnes reconnues: {(preview?.mappedFields || []).join(', ')}</p>
              </div>
            }
            type={newCount > 0 ? 'info' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            columns={previewColumns}
            dataSource={preview?.articles || []}
            rowKey="code_art"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </div>
      )}

      {step === 3 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message={`${importResult?.created || 0} nouveaux articles créés`}
            description={
              importResult?.skippedExisting > 0
                ? `Articles déjà existants (ignorés): ${importResult.skippedExisting}`
                : null
            }
            type={importResult?.created > 0 ? 'success' : 'warning'}
            showIcon
          />

          {importResult?.createdArticles?.length > 0 && (
            <Alert
              message="Articles créés"
              description={
                <div>
                  {importResult.createdArticles.slice(0, 30).map((code) => (
                    <Tag key={code} color="green">{code}</Tag>
                  ))}
                  {importResult.createdArticles.length > 30 && (
                    <span>... et {importResult.createdArticles.length - 30} autres</span>
                  )}
                </div>
              }
              type="success"
            />
          )}

          {importResult?.errors?.length > 0 && (
            <Alert
              message={`${importResult.errors.length} erreurs lors de la création`}
              description={
                <div>
                  {importResult.errors.slice(0, 5).map(({ code_art, error }) => (
                    <p key={code_art} style={{ margin: 0 }}>
                      <Tag color="red">{code_art}</Tag> {error}
                    </p>
                  ))}
                  {importResult.errors.length > 5 && (
                    <span>... et {importResult.errors.length - 5} autres</span>
                  )}
                </div>
              }
              type="error"
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default ArticleDBFImportModal;
